import crypto from 'crypto';
import { BaseScraper, ScraperOptions } from '../base';
import { ListingScraper } from '../base/listing.scraper';
import { ApiItemResponse, ApiListResponse } from '../types';
import {
  BaseFetchParams,
  Company,
  CompanyShort,
  GetCompanyPostsParams,
  GetLinkedinAdParams,
  GetLinkedinCompanyParams,
  GetLinkedinJobParams,
  GetLinkedinPostCommentParams,
  GetLinkedinPostCommentReactionsParams,
  GetLinkedinPostCommentRepliesParams,
  GetLinkedinPostCommentsParams,
  GetLinkedinPostParams,
  GetLinkedinPostReactionsParams,
  GetLinkedinProfileCommentsParams,
  GetLinkedInProfileParams,
  GetLinkedinProfileReactionsParams,
  GetProfilePostsParams,
  Job,
  JobShort,
  LinkedInAd,
  LinkedinAdShort,
  PostComment,
  PostReaction,
  PostShort,
  Profile,
  ProfileReaction,
  ProfileServiceShort,
  ProfileShort,
  ScrapeLinkedinCompaniesParams,
  ScrapeLinkedinJobsParams,
  ScrapeLinkedinPostCommentRepliesParams,
  ScrapeLinkedinPostCommentsParams,
  ScrapeLinkedinPostReactionsParams,
  ScrapeLinkedinPostsParams,
  ScrapeLinkedinProfileCommentsParams,
  ScrapeLinkedinProfileReactionsParams,
  ScrapeLinkedinProfilesParams,
  ScrapeLinkedinSalesNavLeadsParams,
  ScrapeLinkedinServicesParams,
  SearchLinkedinAdsParams,
  SearchLinkedinCompaniesParams,
  SearchLinkedinJobsParams,
  SearchLinkedinPostsParams,
  SearchLinkedInProfilesParams,
  SearchLinkedInSalesNavAccountParams,
  SearchLinkedInSalesNavLeadsParams,
  SearchLinkedinServicesParams,
} from './types';

export class LinkedinScraper {
  private scraper: BaseScraper;

  /** @internal */
  constructor(private options: ScraperOptions) {
    this.scraper = new BaseScraper(options);
  }

  async getProfile(
    params: BaseFetchParams & GetLinkedInProfileParams,
  ): Promise<ApiItemResponse<Profile>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile', params });
  }

  async getProfileId(
    params: BaseFetchParams & {
      url?: string;
      publicIdentifier?: string;
    },
  ): Promise<ApiItemResponse<{ id: string }>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile-id', params });
  }

  /** @internal */
  async searchProfileEmail({
    profile,
    ...params
  }: BaseFetchParams & { profile: Partial<Profile> }): Promise<
    ApiItemResponse<{ email?: string }>
  > {
    return this.scraper.fetchApi({
      path: 'linkedin/email-search-by-profile',
      params,
      method: 'POST',
      body: {
        profile,
      },
    });
  }

  async searchProfiles(
    params: BaseFetchParams & SearchLinkedInProfilesParams,
  ): Promise<ApiListResponse<ProfileShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile-search', params });
  }

  async getCompany(
    params: BaseFetchParams & GetLinkedinCompanyParams,
  ): Promise<ApiItemResponse<Company>> {
    return this.scraper.fetchApi({ path: 'linkedin/company', params });
  }

  async searchCompanies(
    params: BaseFetchParams & SearchLinkedinCompaniesParams,
  ): Promise<ApiListResponse<CompanyShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/company-search', params });
  }

  async getJob(params: BaseFetchParams & GetLinkedinJobParams): Promise<ApiItemResponse<Job>> {
    const results = await this.scraper.fetchApi({ path: 'linkedin/job', params });
    return results;
  }

  async searchJobs(
    params: BaseFetchParams & SearchLinkedinJobsParams,
  ): Promise<ApiListResponse<JobShort>> {
    const results = await this.scraper.fetchApi({ path: 'linkedin/job-search', params });
    return results;
  }

  async searchPosts(
    params: BaseFetchParams & SearchLinkedinPostsParams,
  ): Promise<ApiListResponse<PostShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/post-search', params });
  }

  async getProfilePosts(
    params: BaseFetchParams & GetProfilePostsParams,
  ): Promise<ApiListResponse<PostShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile-posts', params });
  }

  async getCompanyPosts(
    params: BaseFetchParams & GetCompanyPostsParams,
  ): Promise<ApiListResponse<PostShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/company-posts', params });
  }

  async getPost(
    params: BaseFetchParams & GetLinkedinPostParams,
  ): Promise<ApiItemResponse<PostShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/post', params });
  }

  async getSinglePostComment(
    params: BaseFetchParams & GetLinkedinPostCommentParams,
  ): Promise<ApiItemResponse<PostComment>> {
    return this.scraper.fetchApi({ path: 'linkedin/post-comment', params });
  }

  async getPostReactions(
    params: BaseFetchParams & GetLinkedinPostReactionsParams,
  ): Promise<ApiListResponse<PostReaction>> {
    return this.scraper.fetchApi({ path: 'linkedin/post-reactions', params });
  }

  async getPostComments(
    params: BaseFetchParams & GetLinkedinPostCommentsParams,
  ): Promise<ApiListResponse<PostComment>> {
    return this.scraper.fetchApi({ path: 'linkedin/post-comments', params });
  }

  async getPostCommentReplies(
    params: BaseFetchParams & GetLinkedinPostCommentRepliesParams,
  ): Promise<ApiListResponse<PostComment>> {
    return this.scraper.fetchApi({ path: 'linkedin/post-comment-replies', params });
  }

  async getCommentReactions(
    params: BaseFetchParams & GetLinkedinPostCommentReactionsParams,
  ): Promise<ApiListResponse<PostReaction>> {
    return this.scraper.fetchApi({ path: 'linkedin/comment-reactions', params });
  }

  async getProfileComments(
    params: BaseFetchParams & GetLinkedinProfileCommentsParams,
  ): Promise<ApiListResponse<PostComment>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile-comments', params });
  }

  async getProfileReactions(
    params: BaseFetchParams & GetLinkedinProfileReactionsParams,
  ): Promise<ApiListResponse<ProfileReaction>> {
    return this.scraper.fetchApi({ path: 'linkedin/profile-reactions', params });
  }

  async scrapeJobs({ query, ...options }: ScrapeLinkedinJobsParams) {
    return new ListingScraper<JobShort, Job>({
      fetchList: (listParams) => this.searchJobs({ ...query, ...listParams }),
      fetchItem: async ({ item, ...rest }) =>
        item?.id ? this.getJob({ jobId: item.id, ...rest }) : { skipResult: true },
      scrapeDetails: true,
      entityName: 'jobs',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 40,
    }).scrapeStart();
  }

  async scrapeCompanies({ query, ...options }: ScrapeLinkedinCompaniesParams) {
    return new ListingScraper<CompanyShort, Company>({
      fetchList: (listParams) => this.searchCompanies({ ...query, ...listParams }),
      fetchItem: async ({ item, ...rest }) =>
        item?.universalName
          ? this.getCompany({
              universalName: item.universalName,
              scrapePeopleTab: options?.scrapePeopleTab,
              ...rest,
            })
          : { skipResult: true },
      scrapeDetails: true,
      entityName: 'companies',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapeProfiles({ query, findEmail, ...options }: ScrapeLinkedinProfilesParams) {
    return new ListingScraper<ProfileShort, Profile>({
      fetchList: (listParams) => this.searchProfiles({ ...query, ...listParams }),
      fetchItem: async ({ item, ...rest }) =>
        item?.publicIdentifier
          ? this.getProfile({ publicIdentifier: item.publicIdentifier, findEmail, ...rest })
          : { skipResult: true },
      scrapeDetails: true,
      entityName: 'profiles',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapePosts({ query, ...options }: ScrapeLinkedinPostsParams) {
    return new ListingScraper<PostShort, PostShort>({
      fetchList: (listParams) => this.searchPosts({ ...query, ...listParams }),
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<PostShort>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'posts',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapePostReactions({ query, ...options }: ScrapeLinkedinPostReactionsParams) {
    return new ListingScraper<PostReaction, PostReaction>({
      fetchList: (listParams) => this.getPostReactions({ ...query, ...listParams }),
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<PostReaction>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'post-reactions',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapePostComments({ query, ...options }: ScrapeLinkedinPostCommentsParams) {
    return new ListingScraper<PostComment, PostComment>({
      fetchList: (fetchArgs) => this.getPostComments({ ...query, ...fetchArgs }),
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<PostComment>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'post-comments',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapePostCommentReplies({ query, ...options }: ScrapeLinkedinPostCommentRepliesParams) {
    return new ListingScraper<PostComment, PostComment>({
      fetchList: (fetchArgs) => this.getPostCommentReplies({ ...query, ...fetchArgs }),
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<PostComment>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'post-comment-reply',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapeProfileComments({ query, ...options }: ScrapeLinkedinProfileCommentsParams) {
    return new ListingScraper<PostComment, PostComment>({
      fetchList: (fetchArgs) => this.getProfileComments({ ...query, ...fetchArgs }),
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<PostComment>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'profile-comments',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async scrapeProfileReactions({ query, ...options }: ScrapeLinkedinProfileReactionsParams) {
    return new ListingScraper<ProfileReaction, ProfileReaction>({
      fetchList: (fetchArgs) => {
        return this.getProfileReactions({ ...query, ...fetchArgs });
      },
      fetchItem: async ({ item }) =>
        item?.id
          ? ({ entityId: item?.id, element: item } as ApiItemResponse<ProfileReaction>)
          : { skipResult: true },
      scrapeDetails: false,
      entityName: 'profile-reactions',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async searchSalesNavigatorLeads(
    params: BaseFetchParams & SearchLinkedInSalesNavLeadsParams,
  ): Promise<ApiListResponse<ProfileShort>> {
    return this.scraper.fetchApi({ path: 'linkedin-sales-nav/lead-search', params });
  }

  async scrapeSalesNavigatorLeads({
    query,
    findEmail,
    ...options
  }: ScrapeLinkedinSalesNavLeadsParams) {
    return new ListingScraper<ProfileShort, Profile>({
      fetchList: (listParams) => this.searchSalesNavigatorLeads({ ...query, ...listParams }),
      fetchItem: async ({ item, ...rest }) => {
        return item?.id
          ? this.getProfile({ profileId: item.id, findEmail, ...rest })
          : { skipResult: true };
      },
      getFetchListParams: () => ({}),
      scrapeDetails: true,
      entityName: 'profiles',
      warnPageLimit: true,
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async searchSalesNavigatorAccounts(
    params: BaseFetchParams & SearchLinkedInSalesNavAccountParams,
  ): Promise<ApiListResponse<ProfileShort>> {
    return this.scraper.fetchApi({ path: 'linkedin-sales-nav/account-search', params });
  }

  async getGroup(
    params: BaseFetchParams & { groupId?: string; url?: string },
  ): Promise<ApiItemResponse<{ name: string; url: string }>> {
    return this.scraper.fetchApi({ path: 'linkedin/group', params });
  }

  async searchGroups(
    params: BaseFetchParams & { search: string; page?: number },
  ): Promise<ApiListResponse<{ name: string; url: string }>> {
    return this.scraper.fetchApi({ path: 'linkedin/group-search', params });
  }

  async searchServices(
    params: BaseFetchParams & SearchLinkedinServicesParams,
  ): Promise<ApiListResponse<ProfileServiceShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/service-search', params });
  }

  async scrapeServices({ query, findEmail, ...options }: ScrapeLinkedinServicesParams) {
    return new ListingScraper<ProfileServiceShort, ProfileServiceShort & Profile>({
      fetchList: (listParams) => this.searchServices({ ...query, ...listParams }),
      fetchItem: async ({ item }) => {
        const profileResult = await this.getProfile({ url: item.linkedinProfileUrl, findEmail });
        profileResult.element = {
          ...item,
          ...profileResult.element,
        };
        return profileResult as ApiItemResponse<ProfileServiceShort & Profile>;
      },
      scrapeDetails: true,
      entityName: 'services',
      sessionId: crypto.randomUUID(),
      ...options,
      maxPageNumber: 100,
    }).scrapeStart();
  }

  async searchAds(
    params: BaseFetchParams & SearchLinkedinAdsParams,
  ): Promise<ApiListResponse<LinkedinAdShort>> {
    return this.scraper.fetchApi({ path: 'linkedin/ad-search', params });
  }

  async getAd(params: BaseFetchParams & GetLinkedinAdParams): Promise<ApiItemResponse<LinkedInAd>> {
    return this.scraper.fetchApi({ path: 'linkedin/ad', params });
  }

  async sendConnection(
    params: BaseFetchParams & { profile: string },
  ): Promise<ApiListResponse<any>> {
    return this.scraper.fetchApi({ path: 'linkedin/send-connection', params, method: 'POST' });
  }

  async getMyReceivedConnections(params: BaseFetchParams): Promise<ApiItemResponse<any>> {
    return this.scraper.fetchApi({ path: 'linkedin/received-connections', params });
  }

  async getMySentConnections(params: BaseFetchParams): Promise<ApiListResponse<any>> {
    return this.scraper.fetchApi({ path: 'linkedin/sent-connections', params });
  }

  async acceptMyConnectionInvitation(
    params: BaseFetchParams & {
      invitationId?: string;
      sharedSecret?: string;
    },
  ): Promise<ApiListResponse<any>> {
    return this.scraper.fetchApi({
      path: 'linkedin/accept-connection',
      params,
      method: 'POST',
    });
  }

  async getMyProfile(params: BaseFetchParams): Promise<ApiItemResponse<Profile>> {
    return this.scraper.fetchApi({ path: 'linkedin/my-profile', params });
  }

  async sendMessage(
    params: BaseFetchParams & {
      recipientProfile: string;
      message: string;
    },
  ): Promise<ApiListResponse<any>> {
    return this.scraper.fetchApi({ path: 'linkedin/send-message', params, method: 'POST' });
  }

  async follow(
    params: BaseFetchParams & {
      targetUrl: string;
    },
  ): Promise<ApiListResponse<any>> {
    return this.scraper.fetchApi({ path: 'linkedin/follow', params, method: 'POST' });
  }

  /** @internal */
  async test() {
    return this.scraper.fetchApi({ path: 'linkedin/test' });
  }
}
